import { Checkbox, Stack, Text, Group, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

type Props = {
  form: any;
  setHaSidoObjetoDeAcosoLaboral: (haSidoObjetoDeAcoso: boolean) => void;
};

const Component = ({ form, setHaSidoObjetoDeAcosoLaboral }: Props) => {
  const { t } = useTranslation();

  // Combined harassment state
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [noneSelected, setNoneSelected] = useState(false);

  const manifestacionesAcosoLaboral = [
    "Comentarios o bromas sobre características personales (raza, estatus social, apariencia física, forma de hablar).",
    "Creación o difusión de rumores sobre la vida privada de una persona.",
    "Comportamientos o mensajes amenazantes hacia una persona (verbal, escrito, digital).",
    "Comportamientos que aíslan a una persona, como ignorar su presencia, no hablarle o impedirle hacerlo.",
    "Críticas constantes al desempeño profesional o amenazas de medidas disciplinarias efectuadas de forma hostil.",
    "Modificar condiciones laborales (horario, lugar, funciones, permisos) de forma injustificada, recurrente o diferenciada respecto al resto del personal.",
    "No asignar trabajo a una persona o darle tareas inútiles, absurdas o con exigencias desproporcionadas.",
  ];

  const workplaceOptions = manifestacionesAcosoLaboral.map(
    (_, index) => `AT_${String(index + 1).padStart(3, "0")}AL`
  );

  // Combined manifestations array
  const allManifestaciones = [...manifestacionesAcosoLaboral];

  // Generate combined options
  const allOptions = [...workplaceOptions];
  const noneOption = "AT_001_NONE";

  // Update form and parent state for combined harassment
  useEffect(() => {
    if (noneSelected) {
      // When "Ninguna de las anteriores" is selected, set N.A values
      form.setFieldValue("AT_001_007AL", ["N.A"]);
      setHaSidoObjetoDeAcosoLaboral(false);
    } else {
      // Normal behavior when specific manifestations are selected
      const workplaceValues = selectedValues.filter((value) =>
        workplaceOptions.includes(value)
      );
      form.setFieldValue("AT_001_007AL", workplaceValues);
      setHaSidoObjetoDeAcosoLaboral(workplaceValues.length > 0);
    }
  }, [selectedValues, noneSelected]);

  const handleCheckboxChange = (value: string) => {
    if (value === noneOption) {
      if (noneSelected) {
        setNoneSelected(false);
        setSelectedValues([]);
      } else {
        setNoneSelected(true);
        setSelectedValues([noneOption]);
      }
    } else {
      setNoneSelected(false);
      if (selectedValues.includes(value)) {
        setSelectedValues((prev) =>
          prev.filter((v) => v !== value && v !== noneOption)
        );
      } else {
        setSelectedValues((prev) => [
          ...prev.filter((v) => v !== noneOption),
          value,
        ]);
      }
    }
  };

  return (
    <Stack spacing="sm">
      <Text fw={500} size="sm" color="gray.8">
        En relación a la organización en la que estás actualmente, ¿en los
        últimos 12 meses has experimentado alguna de estas situaciones? Esto
        puede involucrar a personal de tu compañía como a personal de terceros o
        usuarios o clientes.
      </Text>
      <Text fz="xs" color="red">
        *Debes seleccionar al menos una opción.
      </Text>

      <Stack spacing={4}>
        {allManifestaciones.map((manifestacion, index) => (
          <Checkbox
            key={allOptions[index]}
            label={manifestacion}
            checked={selectedValues.includes(allOptions[index])}
            disabled={noneSelected}
            onChange={() => handleCheckboxChange(allOptions[index])}
            size="sm"
            styles={{
              body: { alignItems: "center" },
              label: {
                lineHeight: 1.4,
                minHeight: "2.8rem",
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        ))}
        <Checkbox
          label="Ninguna de las anteriores"
          checked={noneSelected}
          onChange={() => handleCheckboxChange(noneOption)}
          size="sm"
          color="gray"
          styles={{
            body: { alignItems: "center" },
            label: {
              lineHeight: 1.4,
              minHeight: "2.8rem",
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

export default Component;
